import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, RefreshCw, Loader2, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BooksManagement = () => {
  const { getAuthHeader } = useAuth();
  const [books, setBooks] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [preventClose, setPreventClose] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    author: '',
    coverImage: '',
    price: 0,
    ageRange: '',
    pages: 0,
    category: '',
    description: '',
    featured: false,
    themes: '',
    pdfUrl: '',
    activitySheetsUrl: '',
    activitySheetsDescription: '',
    teacherCompanionUrl: '',
    teacherCompanionDescription: ''
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [replacingPdf, setReplacingPdf] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [activityFile, setActivityFile] = useState(null);
  const [companionFile, setCompanionFile] = useState(null);

  const loadBooks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/books?active_only=false`, {
        headers: getAuthHeader()
      });
      setBooks(response.data);
    } catch (error) {
      toast.error('Failed to load books');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!file.name.endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploadingPdf(true);
    setPdfFile(file);
    setPreventClose(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/admin/books/upload-pdf`, formDataUpload, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      // Auto-fill form with extracted data
      const newFormData = {
        ...formData,
        title: response.data.title || '',
        author: response.data.author || '',
        pages: response.data.pages || 0,
        coverImage: response.data.cover_image || '',
        pdfUrl: response.data.pdf_url || ''
      };

      setFormData(newFormData);

      toast.success('✅ PDF processed! Title, author, and cover extracted. Please complete the remaining fields.', {
        duration: 5000
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process PDF');
      setPreventClose(false);
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, or WebP)');
      return;
    }

    setUploadingCover(true);
    setCoverFile(file);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/admin/upload-cover`, formDataUpload, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update form with uploaded cover URL
      setFormData({ ...formData, coverImage: response.data.coverUrl });
      
      // Show appropriate message
      const message = response.data.format === 'JPEG' && file.type === 'image/png' 
        ? 'Cover uploaded! (PNG converted to JPEG for better performance)' 
        : 'Cover image uploaded successfully!';
      toast.success(message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload cover image');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleReplacePdf = async (event) => {
    const file = event.target.files[0];
    if (!file || !file.name.endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    if (!editingBook) return;

    setReplacingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/admin/books/${editingBook.id}/replace-pdf`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });

      // Update form with new data
      setFormData(prev => ({
        ...prev,
        pdfUrl: res.data.pdf_url || prev.pdfUrl,
        pages: res.data.pages || prev.pages,
        coverImage: res.data.cover_image || prev.coverImage,
      }));

      toast.success(`PDF replaced — ${res.data.pages} pages processed. Save to confirm other changes.`, { duration: 5000 });
    } catch (error) {
      const d = error.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join(', ') : (typeof d === 'string' ? d : 'Failed to replace PDF');
      toast.error(msg);
    } finally {
      setReplacingPdf(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.author || !formData.category) {
      toast.error('Please fill in all required fields (title, author, category)');
      return;
    }

    if (!formData.price || formData.price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    const bookData = {
      ...formData,
      price: parseFloat(formData.price),
      pages: parseInt(formData.pages) || 0,
      themes: formData.themes.split(',').map(t => t.trim()).filter(t => t)
    };

    try {
      let bookId = editingBook?.id;
      
      if (editingBook) {
        await axios.put(`${API_URL}/admin/books/${editingBook.id}`, bookData, {
          headers: getAuthHeader()
        });
        toast.success('Book updated successfully');
      } else {
        const response = await axios.post(`${API_URL}/admin/books`, bookData, {
          headers: getAuthHeader()
        });
        bookId = response.data.id;
        toast.success('Book created successfully');
      }

      // Upload activity sheets if selected
      if (activityFile && bookId) {
        try {
          const activityUrl = await handleResourceUpload(bookId, activityFile, 'activity');
          await axios.put(`${API_URL}/admin/books/${bookId}`, {
            ...bookData,
            activitySheetsUrl: activityUrl
          }, {
            headers: getAuthHeader()
          });
          toast.success('Activity sheets uploaded');
        } catch (error) {
          toast.error('Failed to upload activity sheets');
        }
      }

      // Upload teacher companion if selected
      if (companionFile && bookId) {
        try {
          const companionUrl = await handleResourceUpload(bookId, companionFile, 'companion');
          await axios.put(`${API_URL}/admin/books/${bookId}`, {
            ...bookData,
            teacherCompanionUrl: companionUrl
          }, {
            headers: getAuthHeader()
          });
          toast.success('Teacher companion uploaded');
        } catch (error) {
          toast.error('Failed to upload teacher companion');
        }
      }
      
      setPreventClose(false);
      setIsDialogOpen(false);
      resetForm();
      await loadBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save book');
    }
  };

  const handleResourceUpload = async (bookId, file, resourceType) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('resource_type', resourceType);

      const response = await axios.post(
        `${API_URL}/admin/books/${bookId}/upload-resource?resource_type=${resourceType}`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return response.data.resource_url;
    } catch (error) {
      throw error;
    }
  };

  const handleEdit = (book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      subtitle: book.subtitle || '',
      author: book.author,
      coverImage: book.coverImage,
      price: book.price,
      ageRange: book.ageRange,
      pages: book.pages,
      category: book.category,
      description: book.description,
      featured: book.featured,
      themes: book.themes.join(', '),
      pdfUrl: book.pdfUrl || '',
      activitySheetsUrl: book.activitySheetsUrl || '',
      activitySheetsDescription: book.activitySheetsDescription || '',
      teacherCompanionUrl: book.teacherCompanionUrl || '',
      teacherCompanionDescription: book.teacherCompanionDescription || ''
    });
    setIsDialogOpen(true);
  };

  const toggleActive = async (book) => {
    try {
      const endpoint = book.isActive ? 'deactivate' : 'activate';
      await axios.patch(`${API_URL}/admin/books/${book.id}/${endpoint}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Book ${endpoint}d successfully`);
      loadBooks();
    } catch (error) {
      toast.error('Failed to update book status');
    }
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm('Are you sure you want to permanently delete this book?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/books/${bookId}`, {
        headers: getAuthHeader()
      });
      toast.success('Book deleted successfully');
      loadBooks();
    } catch (error) {
      toast.error('Failed to delete book');
    }
  };

  const resetForm = () => {
    setEditingBook(null);
    setPdfFile(null);
    setActivityFile(null);
    setCompanionFile(null);
    setFormData({
      title: '',
      subtitle: '',
      author: '',
      coverImage: '',
      price: 0,
      ageRange: '',
      pages: 0,
      category: '',
      description: '',
      featured: false,
      themes: '',
      pdfUrl: '',
      activitySheetsUrl: '',
      activitySheetsDescription: '',
      teacherCompanionUrl: '',
      teacherCompanionDescription: ''
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Books Management</h1>
        <Button asChild className="bg-gradient-to-r from-blue-600 to-blue-700">
          <a href="/admin-add-book.html">
            <Plus className="mr-2 h-4 w-4" />
            Add New Book
          </a>
        </Button>
      </div>

      {/* Edit Dialog */}
      <Dialog 
        open={isDialogOpen} 
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            resetForm();
          }
        }}
      >
          <DialogContent 
            className="max-w-2xl max-h-[90vh] overflow-y-auto"
            onInteractOutside={(e) => {
              e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              e.preventDefault();
            }}
          >
            <DialogHeader>
              <DialogTitle>{editingBook ? 'Edit Book' : 'Add New Book'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Replace PDF / Cover — only shown when editing */}
              {editingBook && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" /> Update Book Files
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Replace PDF */}
                    <div>
                      <Label className="text-xs text-blue-700">Replace PDF</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          disabled={replacingPdf}
                          onClick={() => document.getElementById('replacePdfInput').click()}
                          data-testid="replace-pdf-btn"
                        >
                          {replacingPdf ? (
                            <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Processing...</>
                          ) : (
                            <><Upload className="h-3 w-3 mr-1" /> Upload New PDF</>
                          )}
                        </Button>
                        <input
                          id="replacePdfInput"
                          type="file"
                          accept=".pdf"
                          onChange={handleReplacePdf}
                          className="hidden"
                          data-testid="replace-pdf-input"
                        />
                      </div>
                      {formData.pdfUrl && (
                        <p className="text-[10px] text-blue-600 mt-1 flex items-center gap-1">
                          <FileText className="h-3 w-3" /> {formData.pages || '?'} pages loaded
                        </p>
                      )}
                    </div>
                    {/* Replace Cover */}
                    <div>
                      <Label className="text-xs text-blue-700">Replace Cover Image</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          disabled={uploadingCover}
                          onClick={() => document.getElementById('replaceCoverInput').click()}
                          data-testid="replace-cover-btn"
                        >
                          {uploadingCover ? (
                            <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload className="h-3 w-3 mr-1" /> Upload New Cover</>
                          )}
                        </Button>
                        <input
                          id="replaceCoverInput"
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleCoverUpload}
                          className="hidden"
                          data-testid="replace-cover-input"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] text-blue-500">Uploading a new PDF will re-process all pages and update the cover. Existing linked activity packs are preserved.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Title*</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="subtitle">Subtitle</Label>
                  <Input
                    id="subtitle"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="author">Author*</Label>
                  <Input
                    id="author"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category*</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="coverImage">Cover Image*</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      id="coverImage"
                      value={formData.coverImage}
                      onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
                      placeholder="Or enter image URL manually"
                      className="flex-1"
                    />
                    <label className="cursor-pointer">
                      <Button 
                        type="button" 
                        variant="outline"
                        disabled={uploadingCover}
                        onClick={() => document.getElementById('coverFileInput').click()}
                      >
                        {uploadingCover ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      <input
                        id="coverFileInput"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleCoverUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {formData.coverImage && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1">Preview:</p>
                      <img 
                        src={formData.coverImage.startsWith('http') ? formData.coverImage : `${process.env.REACT_APP_BACKEND_URL}${formData.coverImage}`}
                        alt="Cover preview" 
                        className="w-32 h-48 object-cover rounded border"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Price*</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="pages">Pages*</Label>
                  <Input
                    id="pages"
                    type="number"
                    value={formData.pages}
                    onChange={(e) => setFormData({ ...formData, pages: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="ageRange">Age Range*</Label>
                  <Input
                    id="ageRange"
                    placeholder="6-10 years"
                    value={formData.ageRange}
                    onChange={(e) => setFormData({ ...formData, ageRange: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description*</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label htmlFor="themes">Themes (comma-separated)</Label>
                <Input
                  id="themes"
                  placeholder="Adventure, Science, Nature"
                  value={formData.themes}
                  onChange={(e) => setFormData({ ...formData, themes: e.target.value })}
                />
              </div>

              {/* Activity Sheets Section */}
              <div className="col-span-2 border-t pt-4 mt-4">
                <h3 className="font-semibold text-gray-800 mb-3">📝 Activity Sheets (Optional)</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="activityFile">Upload Activity Sheets PDF</Label>
                    <Input
                      id="activityFile"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setActivityFile(e.target.files[0])}
                      className="cursor-pointer"
                    />
                    {formData.activitySheetsUrl && (
                      <p className="text-xs text-green-600 mt-1">Current: {formData.activitySheetsUrl}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="activityDescription">Activity Sheets Description</Label>
                    <Textarea
                      id="activityDescription"
                      value={formData.activitySheetsDescription}
                      onChange={(e) => setFormData({ ...formData, activitySheetsDescription: e.target.value })}
                      placeholder="Describe what's included in the activity sheets..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Teacher Companion Section */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <h3 className="font-semibold text-gray-800 mb-3">👨‍🏫 Teacher Companion Guide (Optional)</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="companionFile">Upload Teacher Companion PDF</Label>
                    <Input
                      id="companionFile"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setCompanionFile(e.target.files[0])}
                      className="cursor-pointer"
                    />
                    {formData.teacherCompanionUrl && (
                      <p className="text-xs text-green-600 mt-1">Current: {formData.teacherCompanionUrl}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="companionDescription">Teacher Companion Description</Label>
                    <Textarea
                      id="companionDescription"
                      value={formData.teacherCompanionDescription}
                      onChange={(e) => setFormData({ ...formData, teacherCompanionDescription: e.target.value })}
                      placeholder="Describe the teacher companion guide contents..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 col-span-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="featured" className="cursor-pointer">Featured Book</Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingBook ? 'Update Book' : 'Create Book'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    if (preventClose) {
                      const shouldClose = window.confirm('You have uploaded a PDF. Are you sure you want to cancel?');
                      if (shouldClose) {
                        setPreventClose(false);
                        setIsDialogOpen(false);
                        resetForm();
                      }
                    } else {
                      setIsDialogOpen(false);
                      resetForm();
                    }
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      {/* Books Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {books.map((book) => (
          <Card key={book.id} className={`overflow-hidden ${!book.isActive ? 'opacity-60' : ''}`}>
            <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" style={{ minHeight: '280px' }}>
              <img
                src={book.coverImage}
                alt={book.title}
                className="w-full h-full object-contain p-3"
              />
              {!book.isActive && (
                <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold shadow-md">
                  INACTIVE
                </div>
              )}
              {book.featured && (
                <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded text-xs font-semibold shadow-md">
                  FEATURED
                </div>
              )}
            </div>
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-800 mb-1 line-clamp-1">{book.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{book.author}</p>
              <p className="text-sm text-blue-600 mb-3">${book.price}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(book)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleActive(book)}
                  className={book.isActive ? 'text-orange-600' : 'text-green-600'}
                >
                  {book.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(book.id)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default BooksManagement;